<script setup lang="ts">
import { FlyoutWithOverlay, FormInputWithUnitField } from '@/components/shared'
import { CostUnitLabelType, CostUnitType } from '@/enum'
import useGlobalStore from '@/stores/modules/global'
import CONSTANTS from '@/utils/constants/string-constants'
import { generateIterableListFromEnum } from '@/utils/helpers'
import { computed, onMounted, ref, watch } from 'vue'
import { BaseButton } from '../base'
import useAccountStore from '@/stores/modules/account'

const globalStore = useGlobalStore()
const accountStore = useAccountStore()

const costUnits = computed(() => generateIterableListFromEnum(CostUnitType, CostUnitLabelType))

const dailyCostRules = ref([
  (v: number | string) => !!v || CONSTANTS.PRICE_REQUIRED_RULE,
  (v: number) => v >= 0 || CONSTANTS.POSITIVE_FORMAT_RULE
])
const formInPageRef = ref()
const isSubmitClicked = ref(false)

const handleFormSubmission = async () => {
  const formValidityPromise = await formInPageRef.value.validate()
  if (formValidityPromise.valid) {
    isSubmitClicked.value = true
    accountStore.createNewConfigPrice()
  }
  setTimeout(() => {
    isSubmitClicked.value = false
  }, 1000)
}

watch(
  () => globalStore.showConfigFlyout,
  () => {
    accountStore.resetConfigForm()
    if (globalStore.showConfigFlyout) {
      accountStore.getPricesConfigDetails()
    }
  }
)
</script>
<template>
  <FlyoutWithOverlay v-model="globalStore.showConfigFlyout" flyout-class="d-flex flex-column">
    <template #flyout-header>
      <h6 class="heading-3 semibold">{{ CONSTANTS.SETTING }}</h6>
    </template>
    <v-form ref="formInPageRef" class="d-flex flex-column pt-15 h-100 body-1--new">
      <div class="flex-grow-1 px-5">
        <div v-if="!accountStore.isLoadingPriceConfig" class="ga-9 d-flex flex-column">
          <FormInputWithUnitField
            :label="'Fuel Price'"
            :placeholder="'ex. 24'"
            type="number"
            :units="costUnits"
            :rules="dailyCostRules"
            v-model:value="accountStore.configForm.fuelPrice.value"
            v-model:unit="accountStore.configForm.fuelPrice.unit"
          />
          <FormInputWithUnitField
            :label="'AdBlue Price'"
            :placeholder="'ex. 24'"
            type="number"
            :units="costUnits"
            :rules="dailyCostRules"
            v-model:value="accountStore.configForm.defPrice.value"
            v-model:unit="accountStore.configForm.defPrice.unit"
          />
        </div>
        <div v-else class="d-flex flex-column mt-n5">
          <div>
            <div class="pb-2"><v-skeleton-loader width="150" loading :height="20" /></div>
            <v-skeleton-loader loading :height="40" />
          </div>
          <div class="pt-6">
            <div class="pb-2"><v-skeleton-loader width="150" loading :height="20" /></div>
            <v-skeleton-loader loading :height="40" />
          </div>
        </div>
      </div>
      <div class="pa-5 py-4 border-top">
        <BaseButton
          class="text-white bg-emerald_600"
          :loading="isSubmitClicked"
          :disabled="isSubmitClicked || accountStore.isLoadingPriceConfig"
          @click="handleFormSubmission"
          width="100%"
          >{{ CONSTANTS.SAVE_CHANGES }}</BaseButton
        >
      </div>
    </v-form>
  </FlyoutWithOverlay>
</template>
<style scoped lang="scss"></style>
